/**
 * 섹션 전용 페이지 — 첫 화면 레일에서 넘친 것을 **그리드**로 전부 본다 (261001-client-works-report).
 *
 * ★ 레일로 두지 않는 이유: 카드 수십 장짜리 레일은 **가로로 한없이 길어져** 끝까지 밀어야 마지막이 보인다.
 * ★ **브랜드 하나 관점**이다 — 주소의 브랜드(`?brand=`, 보고서에서 따라온다)를 본다.
 */
import { works } from '@geo-admin/shared/contract/api/index.ts';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { CyclePicker } from '../components/CyclePicker.tsx';
import { Deliverables, SECTIONS, type SectionKey } from '../components/Deliverables.tsx';
import { ErrorBox } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { useAppBrand } from '../lib/appBrand.ts';
import { useCycleBack } from '../lib/works.ts';

export function WorksPage() {
  const { section = 'homepage' } = useParams({ strict: false }) as { section?: string };
  const { brand: wanted } = useSearch({ strict: false }) as { brand?: string };
  const appBrand = useAppBrand(wanted);
  const brandId = appBrand.current?.status === 'active' ? appBrand.current.brandId : '';
  const key = (section in SECTIONS ? section : 'homepage') as SectionKey;
  // 늘 브랜드를 지목한다 — 브랜드가 여럿이면 서버가 요구하고(brand-required), 한 곳이어도 같은 값이다
  // 처음 여는 회차는 서버가 정한다 — 보고서와 같다(261020-report-cycle-yesterday-basis)
  const { back, param, setBack, learn } = useCycleBack(brandId);
  const q = useApi(works.getWorks, { query: { cycle: param, brandId } }, { enabled: brandId !== '' });
  useEffect(() => learn(q.data?.openBack), [q.data?.openBack, learn]);

  return (
    <>
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 border-slate-200 border-b pb-4">
        <div className="flex items-baseline gap-3">
          <Link
            to="/app/report"
            search={brandId ? { brand: brandId } : undefined}
            className="text-slate-400 text-sm hover:text-slate-900"
          >
            ‹ 보고서
          </Link>
          <h1 className="font-bold text-xl">{SECTIONS[key]}</h1>
        </div>
        <CyclePicker cycle={q.data?.cycle} back={back} onBack={setBack} />
      </header>
      {appBrand.loading ? (
        <div className="h-64" />
      ) : !brandId ? (
        <p className="text-slate-500 text-sm">보고서에서 브랜드를 먼저 선택해 주세요</p>
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : q.data ? (
        // 전용 페이지는 **그리드**로, 상한 없이 전부
        <Deliverables data={q.data} grid only={key} />
      ) : (
        <div className="h-64" />
      )}
    </>
  );
}
